import type { ReactNode } from "react";
import { Link } from "react-router";
import { Check, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { ContractingUserStatus } from "@/lib/domain-constants";
import { suretyKeys } from "@/lib/query-keys";
import { useUpdateContractingStatus } from "@/hooks/mutations/useContractingMutations";
import { useAddSuretyClients } from "@/hooks/mutations/useSuretyClientMutations";

const PRIMARY =
  "inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-sm font-bold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:cursor-wait disabled:opacity-70";
const SECONDARY =
  "inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-xl border bg-card px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800";

/** Mete una obra al pipeline de la organización (Mi equipo) como oportunidad nueva. */
export function AddToTeamButton({
  contractingId,
  inPipeline,
}: {
  contractingId: number;
  inPipeline: boolean;
}) {
  const qc = useQueryClient();
  const mutation = useUpdateContractingStatus();

  if (inPipeline || mutation.isSuccess) {
    return (
      <Link to="/surety/team" className={SECONDARY}>
        <Check className="size-4 text-emerald-600" />
        En Mi equipo
      </Link>
    );
  }

  return (
    <button
      type="button"
      disabled={mutation.isPending}
      onClick={() =>
        mutation.mutate(
          { contracting_id: contractingId, status: ContractingUserStatus.SIN_ESTADO },
          {
            onSuccess: () => qc.invalidateQueries({ queryKey: suretyKeys.all }),
            onError: () => toast.error("No se pudo agregar a Mi equipo"),
          },
        )
      }
      className={PRIMARY}
    >
      {mutation.isPending ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <Plus className="size-4" />
      )}
      Agregar a Mi equipo
    </button>
  );
}

/** Suma una empresa a la cartera de la organización. */
export function AddClientButton({ ruc }: { ruc: string }) {
  const mutation = useAddSuretyClients();

  if (mutation.isSuccess) {
    return (
      <Link to="/surety/clients" className={SECONDARY}>
        <Check className="size-4 text-emerald-600" />
        En tu cartera
      </Link>
    );
  }

  return (
    <button
      type="button"
      disabled={mutation.isPending}
      onClick={() =>
        mutation.mutate([ruc], {
          onError: () => toast.error("No se pudo agregar a la cartera"),
        })
      }
      className={PRIMARY}
    >
      {mutation.isPending ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <Plus className="size-4" />
      )}
      Agregar a cartera
    </button>
  );
}

/** Enlace secundario con el mismo tamaño que los botones de acción. */
export function SecondaryLink({
  to,
  children,
  className,
  newTab = false,
}: {
  to: string;
  children: ReactNode;
  className?: string;
  newTab?: boolean;
}) {
  return (
    <Link
      to={to}
      className={cn(SECONDARY, className)}
      {...(newTab && { target: "_blank", rel: "noopener noreferrer" })}
    >
      {children}
    </Link>
  );
}
